<script setup lang="ts">
import { ref, reactive } from 'vue'
import type { FoundationInputType } from "#nuxtware/types/foundation-input-text";

export type ComponentInputSize =  'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
export type ComponentInputColor =  'primary' | 'secondary' | 'tertiary' | '';
export type ComponentInputBackground =  'base-100' | 'base-200' | 'base-300' | '';

export interface ComponentInputProps {
    id: string;
    label: string;
    showLabel?: boolean;
    placeholder?: string;
    type?: FoundationInputType;
    error?: string;
    helperText?: string;
    description?: string;
    size?: ComponentInputSize;
    color?: ComponentInputColor;
    background?: ComponentInputBackground;
    bordered?: boolean;
    inputCss?: string;
    min?: number | string;
    max?: number | string;
    autocomplete?: string;
}

const props = withDefaults(defineProps<ComponentInputProps>(), {
    showLabel: false,
    placeholder: '',
    type: 'text',
    error: '',
    helperText: '',
    description: '',
    size: 'md',
    color: '',
    background: '',
    bordered: false,
    inputCss: '',
    min: undefined,
    max: undefined,
    autocomplete: undefined,
});

const model = defineModel<string>()

const componentInput = ref<HTMLInputElement>()
defineExpose({ componentInput })

const inputSizes = reactive<Record<ComponentInputSize, string>>({
    xs: 'input-xs',
    sm: 'input-sm',
    md: 'input-md',
    lg: 'input-lg',
    xl: 'input-xl',
    '2xl': 'input-2xl',
})

const inputColors = reactive<Record<ComponentInputColor, string>>({
    '': '',
    primary: 'input-primary',
    secondary: 'input-secondary',
    tertiary: 'input-tertiary',
})

const inputBackground = reactive<Record<ComponentInputBackground, string>>({
    '': '',
    'base-100': 'bg-base-100',
    'base-200': 'bg-base-200',
    'base-300': 'bg-base-300',
})

const emit = defineEmits(['onFocus', 'onBlur'])
function onFocus (e: FocusEvent): void { emit('onFocus', e) }
function onBlur (e: FocusEvent): void {
    if (props.type === 'number' && model.value !== undefined && model.value !== '') {
        const num = Number(model.value)
        if (props.max !== undefined && num > Number(props.max)) {
            model.value = String(props.max)
        } else if (props.min !== undefined && num < Number(props.min)) {
            model.value = String(props.min)
        }
    }
    emit('onBlur', e)
}
</script>

<template>
    <div>
        <FoundationLabel
            :for="id"
            class="block mb-1"
            :class="{
                'sr-only': !showLabel,
                'text-error': error
            }"
        >
            {{ label }}
        </FoundationLabel>
        <div class="relative">
            <FoundationInput
                :id="id"
                ref="componentInput"
                v-model="model"
                :type="type"
                class="w-full"
                :class="[
                    'input',
                    inputSizes[size],
                    inputColors[color],
                    inputBackground[background],
                    inputCss,
                    {
                        'input-bordered': bordered,
                        'input-error': error,
                        'pl-9': $slots.iconBefore && !inputCss,
                        'pr-9': $slots.iconAfter && !inputCss,
                    }
                ]"
                :placeholder="placeholder"
                :min="min"
                :max="max"
                :autocomplete="autocomplete"
                :aria-label="!showLabel ? label : null"
                :aria-invalid="error ? 'true' : 'false'"
                :aria-describedby="error ? `${id}-error` : `${id}-description`"
                @focus="onFocus"
                @blur="onBlur"
            />
            <div v-if="$slots.iconBefore" class="absolute inset-y-0 left-0 flex items-center z-20">
                <slot name="iconBefore" />
            </div>
            <div v-if="$slots.iconAfter" class="absolute inset-y-0 right-0 flex items-center z-20">
                <slot name="iconAfter" />
            </div>
        </div>
        <p v-if="error" :id="`${id}-error`" class="mt-2 text-sm text-error">{{ error }}</p>
        <p v-else-if="helperText" class="mt-2 text-sm text-neutral">{{ helperText }}</p>
        <p v-if="description" :id="`${id}-description`" class="sr-only">{{ description }}</p>
    </div>
</template>
